import { useEffect, useRef, useState } from "react"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { getRestaurantItemDetail } from "@/lib/client/api/restaurant"
import { useAuthStore } from "@/lib/store/auth-store"
import { Category, CustomizeItem } from "@/lib/types"
import { useCartItems } from "@/features/cart/hooks/use-cart-detail"
import { addRestaurantItems } from "@/app/actions/order"

import { areCustomizationsEqual } from "../utils/cart-utils"

// Define interfaces for the hook
interface MenuItemDetailDialogProps {
  selectedItemId: number | null
  setSelectedItemId: (id: number | null) => void
  selectedItem: Category["items"][number] | null
  isEditing?: boolean
  restaurantId: string
}

interface FormValues {
  note: string
  [key: string]: any
}

// Define interfaces based on the backend structure
interface CartCustomOption {
  id: number
  name: string
  price: number
  quantity: number
}

interface CartCustomization {
  id: number
  name: string
  options: CartCustomOption[]
}

export function useMenuItemDialog({
  selectedItemId,
  setSelectedItemId,
  selectedItem: placeholder,
  isEditing = false,
  restaurantId,
}: MenuItemDetailDialogProps) {
  const form = useForm<FormValues>({
    defaultValues: {
      note: "",
    },
  })
  const queryClient = useQueryClient()

  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // Track form submission state
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Track invalid customization groups
  const [invalidCustomizations, setInvalidCustomizations] = useState<number[]>(
    []
  )

  // Get authenticated user
  const userId = useAuthStore((state) => state.user?.user_id)

  // Get existing cart for this restaurant
  const { restaurantCart } = useCartItems(restaurantId)

  // State for item quantity
  const [quantity, setQuantity] = useState(1)

  // Fetch item details
  const { data, isLoading } = useQuery({
    queryKey: ["dishDetail", selectedItemId],
    queryFn: () => getRestaurantItemDetail(selectedItemId!),
    enabled: !!selectedItemId,
    placeholderData: placeholder ? { data: placeholder } : keepPreviousData,
  })

  const selectedItem = data?.data
  const showSkeleton = isLoading && !placeholder

  // Find existing item in cart to pre-populate customizations
  useEffect(() => {
    // Only pre-populate when in editing mode
    if (!isEditing) {
      return
    }

    if (selectedItemId && restaurantCart?.restaurant_items) {
      // Find matching items in the cart
      const existingItems = restaurantCart.restaurant_items.filter(
        (item: any) => item.item_id === selectedItemId
      )

      // If the item exists in the cart
      if (existingItems.length > 0) {
        // Get the first instance to pre-populate (we could add logic to select a specific one)
        const existingItem = existingItems[0]

        // Set the note
        if (existingItem.note) {
          form.setValue("note", existingItem.note)
        }

        // Set the quantity
        setQuantity(existingItem.quantity)

        // Set the customizations if they exist
        if (
          existingItem.customizes &&
          existingItem.customizes.length > 0 &&
          selectedItem?.customize_item
        ) {
          // For each customization in the cart
          existingItem.customizes.forEach((customize: any) => {
            // Find the matching customization in the item detail
            const customizeItem = selectedItem.customize_item.find(
              (item: any) => item.customize_id === customize.id
            )

            if (customizeItem) {
              // For single selection customizations (radio)
              if (
                customizeItem.is_check_box === 0 &&
                customize.options.length === 1
              ) {
                // Set the value for radio button customizations
                form.setValue(
                  `customize-${customize.id}`,
                  customize.options[0].id
                )
              }
              // For multiple selection customizations (checkbox)
              else if (
                customizeItem.is_check_box === 1 &&
                customize.options.length > 0
              ) {
                // Set the values for checkbox customizations
                const optionIds = customize.options.map(
                  (option: any) => option.id
                )
                form.setValue(`customize-${customize.id}`, optionIds)
              }
            }
          })
        }
      }
    }
  }, [selectedItemId, restaurantCart, selectedItem, form, isEditing])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (selectedItemId === null) {
      setHasSubmitted(false)
      setInvalidCustomizations([])
      setQuantity(1)
      form.reset()
    }
  }, [selectedItemId, form])

  // Scroll to the first invalid customization
  useEffect(() => {
    if (invalidCustomizations.length > 0 && scrollAreaRef.current) {
      const firstInvalidId = invalidCustomizations[0]
      const invalidElement = document.getElementById(
        `customize-${firstInvalidId}`
      )

      if (invalidElement) {
        // Add offset to account for sticky header (approx 70px)
        const scrollContainer = scrollAreaRef.current.querySelector(
          "[data-radix-scroll-area-viewport]"
        )
        const elementTop = invalidElement.offsetTop

        if (scrollContainer) {
          // Scroll with offset to prevent the sticky header from overlapping the element
          scrollContainer.scrollTo({
            top: Math.max(0, elementTop - 100),
            behavior: "smooth",
          })
        }
      }
    }
  }, [invalidCustomizations])

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async ({
      items,
      note,
      cartId,
    }: {
      items: any[]
      note: string
      cartId?: string
    }) => {
      if (!userId || !restaurantId) {
        throw new Error("Missing user or restaurant information")
      }

      return addRestaurantItems(userId.toString(), restaurantId, items, {
        latitude: 0,
        longitude: 0,
        cartId,
      })
    },
    onSuccess: () => {
      // Close the dialog
      setSelectedItemId(null)

      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })

      // Show success message
      toast.success("Thêm vào giỏ hàng thành công", {
        description: "Món ăn đã được thêm vào giỏ hàng",
      })
    },
    onError: (error) => {
      toast.error("Không thể thêm vào giỏ hàng", {
        description:
          error instanceof Error
            ? error.message
            : "Đã xảy ra lỗi khi thêm món ăn vào giỏ hàng",
      })
    },
  })

  // Handle form submission
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setHasSubmitted(true)

    if (!selectedItem) return

    // If quantity is zero, we want to remove the item from the cart
    if (quantity === 0) {
      if (restaurantCart?.cart_id && selectedItemId) {
        // Prepare all items except the one being removed
        const remainingItems = restaurantCart.restaurant_items
          .filter((item: any) => item.item_id !== selectedItemId)
          .map((item: any) => ({
            item_id: item.item_id.toString(),
            quantity: item.quantity,
            price: item.price,
            note: item.note || "",
            customizes: (item.customizes || []).map((customize: any) => ({
              id: customize.id.toString(),
              name: customize.name,
              options: customize.options.map((option: any) => ({
                id: option.id.toString(),
                quantity: option.quantity,
                price: option.price,
                name: option.name,
              })),
            })),
          }))

        // Update cart without the removed item
        addToCartMutation.mutate({
          items: remainingItems,
          note: "",
          cartId: restaurantCart.cart_id.toString(),
        })
        return
      }
    }

    // Get form data
    const formData = new FormData(event.target as HTMLFormElement)
    const note = (formData.get("note") as string) || ""

    // Get customizations
    const missingCustomizations: CustomizeItem[] = []
    const invalidIds: number[] = []
    const selectedCustomizations: CartCustomization[] = []

    // Process customizations
    selectedItem.customize_item?.forEach((item: CustomizeItem) => {
      if (item.is_required && item.customize_active === 1) {
        // Get selected options for this customization group
        const selectedOptions: CartCustomOption[] = []

        // For radio buttons (single selection)
        if (item.is_check_box === 0) {
          // Get directly from React Hook Form state instead of formData
          const optionId = form.getValues(`customize-${item.customize_id}`)

          if (optionId) {
            const option = item.customize_options?.find(
              (opt) => opt.customize_option_id === Number(optionId)
            )
            if (option) {
              selectedOptions.push({
                id: option.customize_option_id,
                name: option.customize_option_name,
                price: option.customize_price || 0,
                quantity: 1,
              })
            }
          }
        }
        // For checkboxes (multiple selection)
        else {
          // Get directly from React Hook Form state
          const selectedOptionIds =
            form.getValues(`customize-${item.customize_id}`) || []

          // Add the selected options to the cart
          if (
            Array.isArray(selectedOptionIds) &&
            selectedOptionIds.length > 0
          ) {
            selectedOptionIds.forEach((optionId) => {
              const option = item.customize_options?.find(
                (opt) => opt.customize_option_id === optionId
              )

              if (option) {
                selectedOptions.push({
                  id: option.customize_option_id,
                  name: option.customize_option_name,
                  price: option.customize_price || 0,
                  quantity: 1,
                })
              }
            })
          }
        }

        // Check if required customizations are selected
        const hasEnoughOptions =
          selectedOptions.length > 0 &&
          (!item.customize_item_lower_limit ||
            selectedOptions.length >= item.customize_item_lower_limit)

        if (!hasEnoughOptions) {
          missingCustomizations.push(item)
          invalidIds.push(item.customize_id)
        } else {
          selectedCustomizations.push({
            id: item.customize_id,
            name: item.customize_item_name,
            options: selectedOptions,
          })
        }
      } else if (item.customize_active === 1) {
        // Handle non-required customizations that have selections
        const selectedOptions: CartCustomOption[] = []

        // For radio buttons (single selection)
        if (item.is_check_box === 0) {
          const optionId = form.getValues(`customize-${item.customize_id}`)

          if (optionId) {
            const option = item.customize_options?.find(
              (opt) => opt.customize_option_id === Number(optionId)
            )
            if (option) {
              selectedOptions.push({
                id: option.customize_option_id,
                name: option.customize_option_name,
                price: option.customize_price || 0,
                quantity: 1,
              })
            }
          }
        }
        // For checkboxes (multiple selection)
        else {
          const selectedOptionIds =
            form.getValues(`customize-${item.customize_id}`) || []

          if (
            Array.isArray(selectedOptionIds) &&
            selectedOptionIds.length > 0
          ) {
            selectedOptionIds.forEach((optionId) => {
              const option = item.customize_options?.find(
                (opt) => opt.customize_option_id === optionId
              )

              if (option) {
                selectedOptions.push({
                  id: option.customize_option_id,
                  name: option.customize_option_name,
                  price: option.customize_price || 0,
                  quantity: 1,
                })
              }
            })
          }
        }

        // Add to cart if there are selections
        if (selectedOptions.length > 0) {
          selectedCustomizations.push({
            id: item.customize_id,
            name: item.customize_item_name,
            options: selectedOptions,
          })
        }
      }
    })

    // Update invalid customizations state
    setInvalidCustomizations(invalidIds)

    // Check for missing required customizations
    if (missingCustomizations.length > 0) {
      toast.error("Vui lòng chọn tùy chọn bắt buộc", {
        description: "Bạn chưa chọn đủ tùy chọn bắt buộc cho món ăn này",
      })
      return
    }

    // Prepare the new item for the cart
    const newCartItem = {
      item_id: selectedItem.restaurant_item_id,
      quantity: quantity,
      price: selectedItem.price || 0,
      customizes: selectedCustomizations,
      note: note,
      item_name: selectedItem.item_name,
    }

    // Check if this is an edit of an existing item
    const isEditingExistingItem =
      selectedItemId &&
      restaurantCart?.restaurant_items.some(
        (item: any) => item.item_id === selectedItemId
      )

    // Collect all items that should be in the cart (existing + new)
    const allCartItems = []

    // Flag to track if we found a matching item
    let foundMatchingItem = false

    // Add existing items from the cart first
    if (
      restaurantCart?.restaurant_items &&
      restaurantCart.restaurant_items.length > 0
    ) {
      // Process each existing item
      restaurantCart.restaurant_items.forEach((item: any) => {
        // Check if this item is identical to the new one (same dish, customizations, and note)
        if (
          item.item_id === newCartItem.item_id &&
          item.note === newCartItem.note &&
          areCustomizationsEqual(item.customizes, newCartItem.customizes)
        ) {
          // Found a match - increase the quantity instead of adding a duplicate
          foundMatchingItem = true

          // If editing an existing item, replace it with the new quantity
          // If adding to cart normally, combine quantities
          if (isEditingExistingItem) {
            allCartItems.push({
              ...item,
              quantity: newCartItem.quantity, // Replace with new quantity
            })
          } else {
            allCartItems.push({
              ...item,
              quantity: item.quantity + newCartItem.quantity, // Combine quantities
            })
          }
        } else if (
          item.item_id === newCartItem.item_id &&
          isEditingExistingItem
        ) {
          // If we're editing this item but the customizations/note changed,
          // replace it entirely with the new version
          foundMatchingItem = true
          allCartItems.push(newCartItem)
        } else {
          // Not a match - add the existing item as-is
          allCartItems.push({
            item_id: item.item_id,
            quantity: item.quantity,
            price: item.price,
            customizes:
              item.customizes?.map((customize: any) => ({
                id: customize.id,
                name: customize.name,
                options: customize.options,
              })) || [],
            note: item.note || "",
            item_name: item.item_name,
          })
        }
      })
    }

    // Only add the new item if we didn't find a matching one
    if (!foundMatchingItem) {
      allCartItems.push(newCartItem)
    }

    // Add to cart, passing existing cart ID if available
    addToCartMutation.mutate({
      items: allCartItems,
      note,
      cartId: restaurantCart?.cart_id?.toString(),
    })
  }

  // Calculate total price including customizations
  const calculateTotal = () => {
    if (!selectedItem) return 0

    // Base price
    let total = (selectedItem.price || 0) * quantity

    // Process customizations to add prices from form state
    selectedItem.customize_item?.forEach((item: CustomizeItem) => {
      // Radio button customizations
      if (item.is_check_box === 0) {
        const optionId = form.watch(`customize-${item.customize_id}`)
        if (optionId) {
          const option = item.customize_options?.find(
            (opt) => opt.customize_option_id === Number(optionId)
          )
          if (option) {
            total += (option.customize_price || 0) * quantity
          }
        }
      }
      // Checkbox customizations
      else {
        const selectedOptionIds =
          form.watch(`customize-${item.customize_id}`) || []
        if (Array.isArray(selectedOptionIds) && selectedOptionIds.length > 0) {
          selectedOptionIds.forEach((optionId) => {
            const option = item.customize_options?.find(
              (opt) => opt.customize_option_id === optionId
            )
            if (option) {
              total += (option.customize_price || 0) * quantity
            }
          })
        }
      }
    })

    return total
  }

  return {
    form,
    scrollAreaRef,
    hasSubmitted,
    invalidCustomizations,
    quantity,
    setQuantity,
    selectedItem,
    showSkeleton,
    handleSubmit,
    calculateTotal,
    addToCartMutation,
  }
}
